/**
 * MachineWizard — cadastro e edição de máquina em etapas (issue #362, D8).
 *
 * Criação: a máquina nasce ao salvar a etapa 1; proteções e adequações dependem do id.
 * Edição: as mesmas etapas, com navegação livre.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, LinearProgress, Paper, Typography } from '@mui/material';
import { useCompanyContext } from '../../../../hooks/useCompanyContext';
import { machineService } from '../../../../services/machineService';
import type { Department } from '../../../../types/department';
import { ConfirmDialog } from './ConfirmDialog';
import {
  EMPTY_IDENTIFICATION,
  IdentificationErrors,
  IdentificationValues,
  MACHINE_WIZARD_STEPS,
  StepHandle,
} from './MachineWizard.types';
import {
  errorMessage,
  identificationToPayload,
  validateIdentification,
} from './machineWizardValidation';
import { AdequaciesStep } from './steps/AdequaciesStep';
import { AppraisalStep } from './steps/AppraisalStep';
import { IdentificationStep } from './steps/IdentificationStep';
import { ProtectionsStep } from './steps/ProtectionsStep';

export interface MachineWizardProps {
  /** Presente na edição; ausente na criação (o id nasce ao salvar a etapa 1). */
  machineId?: string;
  initialValues?: IdentificationValues;
  departments: Department[];
  /** Quem pode mexer em proteções e adequações (`machine.update`). */
  canEditItems: boolean;
  /** Avisa a empresa escolhida na etapa 1, para quem carrega só os setores dela. */
  onCompanyChange?: (companyId: string) => void;
  onFinish: () => void;
  onCancel: () => void;
}

const WHY_BY_STEP = [
  'Salvar guarda os dados da máquina e leva você às proteções instaladas.',
  'Salvar guarda a proteção que você digitou, se houver, e leva você às adequações.',
  'Salvar guarda a adequação que você digitou, se houver, e leva você ao resumo da apreciação.',
  'Concluir leva você de volta à lista de máquinas. Nada se perde: tudo já foi guardado.',
];

export const MachineWizard: React.FC<MachineWizardProps> = ({
  machineId: initialMachineId,
  initialValues = EMPTY_IDENTIFICATION,
  departments,
  canEditItems,
  onCompanyChange,
  onFinish,
  onCancel,
}) => {
  const [machineId, setMachineId] = useState<string | null>(initialMachineId ?? null);
  const [activeStep, setActiveStep] = useState(0);
  const [values, setValues] = useState<IdentificationValues>(initialValues);
  const [savedValues, setSavedValues] = useState<IdentificationValues>(initialValues);
  const [errors, setErrors] = useState<IdentificationErrors>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [stepDirty, setStepDirty] = useState(false);
  const [leaving, setLeaving] = useState<(() => void) | null>(null);
  const stepRef = useRef<StepHandle>(null);

  const isEditing = Boolean(initialMachineId);
  // Sem empresa ativa no seletor do cabeçalho, quem cadastra precisa dizer de quem é a máquina.
  const { shouldShowCompanyField } = useCompanyContext();
  const askCompany = !isEditing && shouldShowCompanyField;
  // Salva a máquina, a empresa dona fica travada: o servidor ignora a troca na atualização.
  const companyLocked = machineId !== null;
  const isLastStep = activeStep === MACHINE_WIZARD_STEPS.length - 1;
  const dirty = activeStep === 0 ? JSON.stringify(values) !== JSON.stringify(savedValues) : stepDirty;

  useEffect(() => {
    onCompanyChange?.(values.companyId);
  }, [onCompanyChange, values.companyId]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const handleDirtyChange = useCallback((value: boolean) => setStepDirty(value), []);
  const handleSaved = useCallback((message: string) => setNotice(message), []);

  /** Executa a saída já; se há dados sem salvar, pergunta antes. */
  const leaveOrAsk = (action: () => void) => {
    if (dirty) setLeaving(() => action);
    else action();
  };

  const goToStep = (index: number) => {
    setError(null);
    setNotice(null);
    setStepDirty(false);
    setActiveStep(index);
  };

  const discardAndLeave = () => {
    if (activeStep === 0) setValues(savedValues);
    const action = leaving;
    setLeaving(null);
    action?.();
  };

  const saveIdentification = async (): Promise<boolean> => {
    const found = validateIdentification(values, askCompany && !companyLocked);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      setError('Corrija os campos marcados para continuar.');
      return false;
    }

    setBusy(true);
    setError(null);
    try {
      if (machineId) {
        await machineService.updateMachine(machineId, identificationToPayload(values, false));
      } else {
        const created = await machineService.createMachine(identificationToPayload(values));
        setMachineId(String(created.id));
      }
      setSavedValues(values);
      return true;
    } catch (err: unknown) {
      setError(errorMessage(err, 'Não foi possível salvar a máquina.'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handlePrimary = async () => {
    if (isLastStep) {
      onFinish();
      return;
    }

    setBusy(true);
    let proceed: boolean;
    try {
      proceed = activeStep === 0 ? await saveIdentification() : (await stepRef.current?.salvarPendente()) ?? true;
    } finally {
      setBusy(false);
    }
    if (!proceed) return;

    goToStep(activeStep + 1);
    if (activeStep === 0) setNotice('Dados da máquina salvos.');
  };

  const handleChange = (patch: Partial<IdentificationValues>) => {
    setValues((previous) => ({ ...previous, ...patch }));
    setErrors((previous) => {
      const next = { ...previous };
      (Object.keys(patch) as Array<keyof IdentificationValues>).forEach((key) => delete next[key]);
      return next;
    });
  };

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return (
          <IdentificationStep
            values={values}
            errors={errors}
            departments={departments}
            askCompany={askCompany}
            companyLocked={companyLocked}
            disabled={busy}
            onChange={handleChange}
          />
        );
      case 1:
        return machineId ? (
          <ProtectionsStep
            ref={stepRef}
            machineId={machineId}
            canEdit={canEditItems}
            onDirtyChange={handleDirtyChange}
            onSaved={handleSaved}
          />
        ) : null;
      case 2:
        return machineId ? (
          <AdequaciesStep
            ref={stepRef}
            machineId={machineId}
            canEdit={canEditItems}
            onDirtyChange={handleDirtyChange}
            onSaved={handleSaved}
          />
        ) : null;
      default:
        return machineId ? <AppraisalStep machineId={machineId} canEdit={canEditItems} /> : null;
    }
  };

  const total = MACHINE_WIZARD_STEPS.length;

  return (
    <Box>
      <Box
        component="nav"
        aria-label="Etapas do cadastro"
        sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: `repeat(${total}, 1fr)` }, gap: 1, mb: 1 }}
      >
        {MACHINE_WIZARD_STEPS.map((step, index) => {
          const reachable = isEditing || machineId !== null || index === 0;
          const state = index === activeStep ? 'Etapa atual' : index < activeStep ? 'Feita' : 'Falta';
          return (
            <Button
              key={step.key}
              variant={index === activeStep ? 'contained' : 'outlined'}
              color="primary"
              disabled={!reachable || busy}
              aria-current={index === activeStep ? 'step' : undefined}
              onClick={() => index !== activeStep && leaveOrAsk(() => goToStep(index))}
              sx={{ minHeight: 56, flexDirection: 'column', alignItems: 'flex-start', textTransform: 'none', textAlign: 'left' }}
            >
              <Typography component="span" sx={{ fontSize: '1rem' }}>
                Etapa {index + 1} de {total} · {state}
              </Typography>
              <Typography component="span" sx={{ fontSize: '1rem', fontWeight: 700 }}>
                {step.label}
              </Typography>
            </Button>
          );
        })}
      </Box>
      <LinearProgress
        variant="determinate"
        value={((activeStep + 1) / total) * 100}
        aria-label="Progresso do cadastro"
        sx={{ mb: 3, height: 8, borderRadius: 4 }}
      />

      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
        {renderStep()}
      </Paper>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          pt: 2,
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="body1">{WHY_BY_STEP[activeStep]}</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="text" onClick={() => leaveOrAsk(onCancel)} disabled={busy} sx={{ minHeight: 44 }}>
            Sair do cadastro
          </Button>
          {activeStep > 0 && (
            <Button
              variant="text"
              onClick={() => leaveOrAsk(() => goToStep(activeStep - 1))}
              disabled={busy}
              sx={{ minHeight: 44 }}
            >
              Voltar
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handlePrimary}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={20} color="inherit" /> : undefined}
            sx={{ minHeight: 44 }}
          >
            {isLastStep ? 'Concluir cadastro' : 'Salvar e continuar'}
          </Button>
        </Box>
      </Box>

      <ConfirmDialog
        open={leaving !== null}
        title="Sair sem salvar?"
        message="Você digitou dados que ainda não foram salvos. Se sair agora, eles se perdem."
        confirmLabel="Sair sem salvar"
        cancelLabel="Continuar editando"
        onConfirm={discardAndLeave}
        onCancel={() => setLeaving(null)}
      />
    </Box>
  );
};

export default MachineWizard;
